import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Building, Plus, ThreeDotsVertical } from 'react-bootstrap-icons';
import {
  useGetEmailAccountsQuery,
  useGetProjectsQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
} from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../../hooks/useTenantIdForPreferences';
import ERMSectionHeader from './ERMSectionHeader';
import {
  buildDefaultDepartments,
  createDepartmentId,
  ErmDepartment,
  normalizeDepartmentAssignments,
  parseErmDepartmentPreferences,
  removeDepartmentFromProjectMap,
} from './departmentPreferences';

interface ErmProject {
  id: string;
  title?: string;
  [key: string]: unknown;
}

interface EmployeeOption {
  email: string;
  fullName: string;
  avatarUrl: string;
}

const compareDepartmentNames = (a: ErmDepartment, b: ErmDepartment): number => (
  String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
);

const normalizeEmail = (value: unknown): string => String(value || '').trim().toLowerCase();

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const record = (error && typeof error === 'object') ? (error as Record<string, any>) : {};
  const data = (record.data && typeof record.data === 'object') ? record.data : {};
  const directMessage = typeof record.message === 'string' ? record.message.trim() : '';
  const dataMessage = typeof data.message === 'string'
    ? data.message.trim()
    : (typeof data.error === 'string' ? data.error.trim() : '');
  return dataMessage || directMessage || fallback;
};

const deriveNameFromEmail = (emailValue: string): string => {
  const local = String(emailValue || '').split('@')[0] || '';
  const normalized = local.replace(/[._-]+/g, ' ').replace(/\d+/g, ' ').trim();
  if (!normalized) return emailValue;
  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getInitials = (value: string): string => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

export default function ERMDepartments() {
  const { currentUser } = useUser();
  const [search, setSearch] = useState('');
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState<string | null>(null);
  const [departmentNameDraft, setDepartmentNameDraft] = useState('');
  const [departmentLeadEmailDraft, setDepartmentLeadEmailDraft] = useState('');
  const [departmentLeadNameDraft, setDepartmentLeadNameDraft] = useState('');
  const [departmentModalError, setDepartmentModalError] = useState('');

  const { data: projectsRaw = [], isLoading: isLoadingProjects } = useGetProjectsQuery({ scope: 'all' });
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const { data: emailAccountsData } = useGetEmailAccountsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateUserPreferences, { isLoading: isSavingStructure }] = useUpdateUserPreferencesMutation();
  const normalizedRole = String(currentUser?.role || '').trim().toLowerCase();
  const isAdminUser = normalizedRole === 'admin' || normalizedRole === 'administrator';

  const projects = useMemo<ErmProject[]>(
    () => (Array.isArray(projectsRaw) ? (projectsRaw as ErmProject[]) : []),
    [projectsRaw]
  );

  const allPreferences = useMemo<Record<string, unknown>>(
    () => toRecord((userPreferencesData as any)?.preferences),
    [userPreferencesData]
  );

  const {
    departments,
    projectDepartmentMap,
    focusedDepartmentIds,
    hasStoredDepartments,
  } = useMemo(
    () => parseErmDepartmentPreferences(allPreferences),
    [allPreferences]
  );

  const didAttemptDepartmentSeedRef = useRef(false);
  useEffect(() => {
    if (didAttemptDepartmentSeedRef.current) return;
    if (!userPreferencesData) return;
    didAttemptDepartmentSeedRef.current = true;
    if (!isAdminUser) return;
    if (hasStoredDepartments) return;
    void updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermDepartments: buildDefaultDepartments(),
      },
    }).unwrap().catch((error) => {
      console.error('Failed to seed default ERM departments:', error);
    });
  }, [hasStoredDepartments, isAdminUser, tenantIdForPrefs, updateUserPreferences, userPreferencesData]);

  const employeeOptions = useMemo<EmployeeOption[]>(() => {
    const rawAccounts = (
      (emailAccountsData as any)?.data?.emailAccounts
      || (emailAccountsData as any)?.emailAccounts
      || []
    ) as any[];
    const dedup = new Map<string, EmployeeOption>();

    rawAccounts.forEach((account) => {
      const fullEmailCandidate = String(account?.fullEmail || account?.email || '').trim();
      const constructedEmail = fullEmailCandidate.includes('@')
        ? fullEmailCandidate
        : `${String(account?.email || '').trim()}@${String(account?.domain || '').trim()}`;
      const email = normalizeEmail(constructedEmail);
      if (!email || !email.includes('@')) return;

      const firstName = String(account?.firstName || '').trim();
      const lastName = String(account?.lastName || '').trim();
      const accountName = String(account?.displayName || account?.name || '').trim();
      const fullName = `${firstName} ${lastName}`.trim() || accountName || deriveNameFromEmail(email);
      const avatarUrl = String(account?.avatar || account?.avatarUrl || '').trim();

      dedup.set(email, {
        email,
        fullName,
        avatarUrl,
      });
    });

    return Array.from(dedup.values()).sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [emailAccountsData]);

  const employeeByEmail = useMemo(() => {
    const map = new Map<string, EmployeeOption>();
    employeeOptions.forEach((employee) => {
      const email = normalizeEmail(employee.email);
      if (!email) return;
      map.set(email, employee);
    });
    return map;
  }, [employeeOptions]);

  const departmentById = useMemo(() => {
    const map = new Map<string, ErmDepartment>();
    departments.forEach((department) => map.set(department.id, department));
    return map;
  }, [departments]);
  const sortedDepartments = useMemo(
    () => [...departments].sort(compareDepartmentNames),
    [departments]
  );

  const validDepartmentIds = useMemo(
    () => new Set(departments.map((department) => department.id)),
    [departments]
  );
  const focusedDepartmentIdSet = useMemo(
    () => new Set(focusedDepartmentIds),
    [focusedDepartmentIds]
  );
  const sortedFocusedDepartmentIds = useMemo(
    () => [...focusedDepartmentIds].sort((left, right) => {
      const leftName = String(departmentById.get(left)?.name || '');
      const rightName = String(departmentById.get(right)?.name || '');
      return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    }),
    [focusedDepartmentIds, departmentById]
  );

  const getProjectDepartmentIds = useCallback((projectIdValue: string): string[] => {
    const projectId = String(projectIdValue || '').trim();
    if (!projectId) return [];
    return normalizeDepartmentAssignments(projectDepartmentMap[projectId], validDepartmentIds);
  }, [projectDepartmentMap, validDepartmentIds]);

  const getProjectDepartmentNames = useCallback((projectIdValue: string): string[] => (
    getProjectDepartmentIds(projectIdValue)
      .map((departmentId) => departmentById.get(departmentId)?.name || '')
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .filter(Boolean)
  ), [departmentById, getProjectDepartmentIds]);

  const projectCountByDepartment = useMemo(() => {
    const counts: Record<string, number> = {};
    departments.forEach((department) => { counts[department.id] = 0; });
    projects.forEach((project) => {
      const projectId = String(project?.id || '').trim();
      if (!projectId) return;
      getProjectDepartmentIds(projectId).forEach((departmentId) => {
        counts[departmentId] = (counts[departmentId] || 0) + 1;
      });
    });
    return counts;
  }, [departments, projects, getProjectDepartmentIds]);

  const filteredDepartments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedDepartments;
    return sortedDepartments.filter((department) => {
      const leadEmail = normalizeEmail(department.leadEmail);
      const leadName = String(department.leadName || '').trim() || employeeByEmail.get(leadEmail)?.fullName || '';
      const haystack = [department.name, leadName, leadEmail].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedDepartments, employeeByEmail, search]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((project) => {
      const projectId = String(project?.id || '').trim();
      const projectName = String(project?.title || '').trim();
      const departmentNames = getProjectDepartmentNames(projectId).join(' ');
      return `${projectName} ${departmentNames}`.toLowerCase().includes(q);
    });
  }, [projects, search, getProjectDepartmentNames]);

  const persistDepartmentStructure = async (partialPreferences: Record<string, unknown>) => {
    if (!isAdminUser) return;
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: partialPreferences,
    }).unwrap();
  };

  const persistDepartmentFocus = async (nextFocusedDepartmentIds: string[]) => {
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermFocusedDepartmentIds: normalizeDepartmentAssignments(nextFocusedDepartmentIds, validDepartmentIds),
      },
    }).unwrap();
  };

  const handleToggleFocusedDepartment = async (departmentId: string) => {
    const nextFocused = focusedDepartmentIdSet.has(departmentId)
      ? focusedDepartmentIds.filter((id) => id !== departmentId)
      : [...focusedDepartmentIds, departmentId];
    try {
      await persistDepartmentFocus(nextFocused);
    } catch (error) {
      console.error('Failed to update focused departments:', error);
    }
  };

  const handleFocusOnlyDepartment = async (departmentId: string) => {
    try {
      await persistDepartmentFocus([departmentId]);
    } catch (error) {
      console.error('Failed to focus department:', error);
    }
  };

  const handleClearFocusedDepartments = async () => {
    try {
      await persistDepartmentFocus([]);
    } catch (error) {
      console.error('Failed to clear focused departments:', error);
    }
  };

  const handleToggleProjectDepartment = async (
    projectIdValue: string,
    departmentId: string,
    checked: boolean
  ) => {
    if (!isAdminUser) return;
    const projectId = String(projectIdValue || '').trim();
    if (!projectId) return;
    const current = getProjectDepartmentIds(projectId);
    const nextIds = checked
      ? [...current, departmentId]
      : current.filter((id) => id !== departmentId);
    const normalized = normalizeDepartmentAssignments(nextIds, validDepartmentIds);
    const nextMap: Record<string, string[]> = { ...projectDepartmentMap };
    if (normalized.length === 0) delete nextMap[projectId];
    else nextMap[projectId] = normalized;
    try {
      await persistDepartmentStructure({ ermProjectDepartments: nextMap });
    } catch (error) {
      console.error('Failed to update project department mapping:', error);
    }
  };

  const resetDepartmentEditor = () => {
    setEditingDepartmentId(null);
    setDepartmentNameDraft('');
    setDepartmentLeadEmailDraft('');
    setDepartmentLeadNameDraft('');
    setDepartmentModalError('');
  };

  const openCreateDepartmentModal = () => {
    if (!isAdminUser) return;
    resetDepartmentEditor();
    setShowDepartmentModal(true);
  };

  const openEditDepartmentModal = (department: ErmDepartment) => {
    if (!isAdminUser) return;
    setEditingDepartmentId(department.id);
    setDepartmentNameDraft(department.name);
    setDepartmentLeadEmailDraft(normalizeEmail(department.leadEmail));
    setDepartmentLeadNameDraft(String(department.leadName || '').trim());
    setShowDepartmentModal(true);
  };

  const closeDepartmentModal = () => {
    setShowDepartmentModal(false);
    resetDepartmentEditor();
  };

  const handleSaveDepartment = async () => {
    if (!isAdminUser) return;
    const name = String(departmentNameDraft || '').trim();
    if (!name) return;
    setDepartmentModalError('');

    const leadEmail = normalizeEmail(departmentLeadEmailDraft);
    const leadName = String(departmentLeadNameDraft || '').trim();
    let nextDepartments: ErmDepartment[] = [];

    if (editingDepartmentId) {
      nextDepartments = departments.map((department) => (
        department.id === editingDepartmentId
          ? {
            ...department,
            name,
            leadEmail: leadEmail || undefined,
            leadName: leadName || undefined,
          }
          : department
      ));
    } else {
      nextDepartments = [
        ...departments,
        {
          id: createDepartmentId(name),
          name,
          leadEmail: leadEmail || undefined,
          leadName: leadName || undefined,
        },
      ];
    }

    try {
      await persistDepartmentStructure({ ermDepartments: nextDepartments });
      closeDepartmentModal();
    } catch (error) {
      console.error('Failed to save department:', error);
      setDepartmentModalError(extractApiErrorMessage(error, 'Unable to save department changes.'));
    }
  };

  const handleDeleteDepartment = async (departmentId: string) => {
    if (!isAdminUser) return;
    if (departments.length <= 1) {
      alert('At least one department is required.');
      return;
    }
    if (!window.confirm('Delete this department? Project mappings will be removed.')) return;

    const nextDepartments = departments.filter((department) => department.id !== departmentId);
    const nextProjectMap = removeDepartmentFromProjectMap(projectDepartmentMap, departmentId);
    const nextFocusedDepartmentIds = focusedDepartmentIds.filter((id) => id !== departmentId);

    try {
      await persistDepartmentStructure({
        ermDepartments: nextDepartments,
        ermProjectDepartments: nextProjectMap,
      });
      await persistDepartmentFocus(nextFocusedDepartmentIds);
    } catch (error) {
      console.error('Failed to delete department:', error);
    }
  };

  const getDepartmentLeadLabel = (department: ErmDepartment): string => {
    const explicitName = String(department.leadName || '').trim();
    if (explicitName) return explicitName;
    const leadEmail = normalizeEmail(department.leadEmail);
    if (leadEmail) {
      const employee = employeeByEmail.get(leadEmail);
      return employee?.fullName || leadEmail;
    }
    return 'Lead not assigned';
  };

  const renderDepartmentLeadAvatar = (department: ErmDepartment, size = 34) => {
    const leadEmail = normalizeEmail(department.leadEmail);
    const employee = leadEmail ? employeeByEmail.get(leadEmail) : undefined;
    const fallbackLabel = department.leadName || employee?.fullName || department.name;
    if (employee?.avatarUrl) {
      return (
        <img
          src={employee.avatarUrl}
          alt={fallbackLabel}
          style={{
            width: size,
            height: size,
            minWidth: size,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1.5px solid #e9ecef',
          }}
        />
      );
    }
    return (
      <div
        className="d-flex align-items-center justify-content-center text-white fw-semibold"
        style={{
          width: size,
          height: size,
          minWidth: size,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0d6efd 0%, #5b8cff 100%)',
          fontSize: '0.72rem',
        }}
      >
        {getInitials(fallbackLabel)}
      </div>
    );
  };

  return (
    <div className="erm-departments">
      <ERMSectionHeader
        title="Departments"
        showHelp
        searchPlaceholder="Search departments or project routing"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="text-muted" style={{ fontSize: '0.84rem' }}>
            Manage your company structure, assign department leads, and map projects to departments.
            {!isAdminUser ? ' Department changes are restricted to admins.' : ''}
          </div>
          <Button
            variant="primary"
            size="sm"
            className="d-flex align-items-center"
            onClick={openCreateDepartmentModal}
            disabled={!isAdminUser}
            title={!isAdminUser ? 'Only admins can edit departments' : undefined}
          >
            <Plus size={12} className="me-1" />
            Department
          </Button>
        </div>

        {focusedDepartmentIds.length > 0 ? (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Focused:</span>
            {sortedFocusedDepartmentIds.map((departmentId) => (
              <Badge key={departmentId} bg="primary" style={{ fontSize: '0.7rem' }}>
                {departmentById.get(departmentId)?.name || departmentId}
              </Badge>
            ))}
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              style={{ fontSize: '0.75rem' }}
              onClick={() => { void handleClearFocusedDepartments(); }}
            >
              Clear
            </Button>
          </div>
        ) : null}

        <Row className="g-3">
          <Col lg={5}>
            <Card className="border">
              <Card.Header className="py-2 d-flex align-items-center justify-content-between">
                <div className="fw-semibold small d-flex align-items-center gap-1">
                  <Building size={14} />
                  Company Structure
                </div>
                {isSavingStructure ? <Spinner animation="border" size="sm" /> : null}
              </Card.Header>
              <Card.Body>
                {filteredDepartments.length === 0 ? (
                  <Alert variant="light" className="small mb-0">
                    No departments found for this search.
                  </Alert>
                ) : (
                  filteredDepartments.map((department) => {
                    const isFocused = focusedDepartmentIdSet.has(department.id);
                    const projectCount = projectCountByDepartment[department.id] || 0;
                    return (
                      <Card
                        key={department.id}
                        className={`mb-2 border-0 shadow-sm ${isFocused ? 'bg-primary-subtle' : ''}`}
                      >
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                              {renderDepartmentLeadAvatar(department)}
                              <div style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-truncate">{department.name}</div>
                                <div className="text-muted small text-truncate">{getDepartmentLeadLabel(department)}</div>
                              </div>
                            </div>
                            {isAdminUser ? (
                              <Dropdown align="end">
                                <Dropdown.Toggle variant="link" size="sm" className="text-dark p-0 text-decoration-none">
                                  <ThreeDotsVertical size={14} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => openEditDepartmentModal(department)}>
                                    Edit department
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => { void handleDeleteDepartment(department.id); }}
                                    disabled={departments.length <= 1}
                                  >
                                    Delete department
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            ) : null}
                          </div>

                          <div className="d-flex align-items-center justify-content-between mt-2 gap-2">
                            <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                              {projectCount} project{projectCount === 1 ? '' : 's'}
                            </Badge>
                            <div className="d-flex gap-1">
                              <Button
                                variant={isFocused ? 'primary' : 'outline-primary'}
                                size="sm"
                                style={{ fontSize: '0.68rem', padding: '0.18rem 0.45rem' }}
                                onClick={() => { void handleToggleFocusedDepartment(department.id); }}
                              >
                                {isFocused ? 'Focused' : 'Include'}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: '0.68rem', padding: '0.18rem 0.45rem' }}
                                onClick={() => { void handleFocusOnlyDepartment(department.id); }}
                              >
                                Only
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="border">
              <Card.Header className="py-2 d-flex align-items-center justify-content-between">
                <div className="fw-semibold small">Project Routing</div>
                <span className="text-muted small">
                  {isLoadingProjects ? 'Loading...' : `${filteredProjects.length} project${filteredProjects.length === 1 ? '' : 's'}`}
                </span>
              </Card.Header>
              <Card.Body>
                {filteredProjects.length === 0 ? (
                  <Alert variant="light" className="small mb-0">
                    No projects found for this search.
                  </Alert>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {filteredProjects.map((project) => {
                      const projectId = String(project.id || '').trim();
                      if (!projectId) return null;
                      const assignedDepartmentIds = getProjectDepartmentIds(projectId);
                      const departmentNames = getProjectDepartmentNames(projectId);
                      return (
                        <Card key={projectId} className="border-0 shadow-sm">
                          <Card.Body className="p-2">
                            <div className="fw-semibold small text-truncate">{project.title || 'Untitled'}</div>
                            <div className="d-flex flex-wrap gap-1 mt-1 mb-2">
                              {departmentNames.length > 0 ? (
                                departmentNames.slice(0, 4).map((name) => (
                                  <Badge key={`${projectId}-${name}`} bg="light" text="dark" style={{ fontSize: '0.68rem' }}>
                                    {name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted small">No department</span>
                              )}
                            </div>
                            <Dropdown autoClose="outside">
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: '0.7rem' }}
                                disabled={!isAdminUser}
                                title={!isAdminUser ? 'Only admins can edit project routing' : undefined}
                              >
                                Assign departments
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="px-2 py-2" style={{ minWidth: 260, maxHeight: 240, overflowY: 'auto' }}>
                                {sortedDepartments.map((department) => (
                                  <Form.Check
                                    key={`${projectId}-${department.id}`}
                                    id={`department-route-${projectId}-${department.id}`}
                                    type="checkbox"
                                    className="small mb-1"
                                    label={department.name}
                                    checked={assignedDepartmentIds.includes(department.id)}
                                    disabled={!isAdminUser}
                                    onChange={(event) => {
                                      void handleToggleProjectDepartment(projectId, department.id, event.target.checked);
                                    }}
                                  />
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Card.Body>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal show={showDepartmentModal} onHide={closeDepartmentModal} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {editingDepartmentId ? 'Edit Department' : 'Add Department'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {departmentModalError ? (
            <Alert variant="danger" className="small">
              {departmentModalError}
            </Alert>
          ) : null}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small">Department name</Form.Label>
              <Form.Control
                value={departmentNameDraft}
                onChange={(event) => setDepartmentNameDraft(event.target.value)}
                placeholder="e.g. Licensing, Programming"
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small">Department lead</Form.Label>
              <Form.Select
                value={departmentLeadEmailDraft}
                onChange={(event) => {
                  const nextEmail = normalizeEmail(event.target.value);
                  setDepartmentLeadEmailDraft(nextEmail);
                  const matchedEmployee = employeeByEmail.get(nextEmail);
                  if (matchedEmployee && !String(departmentLeadNameDraft || '').trim()) {
                    setDepartmentLeadNameDraft(matchedEmployee.fullName);
                  }
                }}
              >
                <option value="">Unassigned</option>
                {employeeOptions.map((employee) => (
                  <option key={employee.email} value={employee.email}>
                    {employee.fullName} ({employee.email})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Avatars come from employee profile photos used on the Agents page.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label className="small">Lead display name (optional)</Form.Label>
              <Form.Control
                value={departmentLeadNameDraft}
                onChange={(event) => setDepartmentLeadNameDraft(event.target.value)}
                placeholder="Override display name"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeDepartmentModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => { void handleSaveDepartment(); }}
            disabled={!departmentNameDraft.trim() || isSavingStructure}
          >
            {isSavingStructure ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
